import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { BookingService } from '@/hotdesk-ui/services/booking.service';
import type { Booking } from 'hotdesk_typescript';

export type UserBookingStatsForTracking = {
    totalBookings: number | null;
    firstBookingDate: string | null;
    lastBookingDate: string | null;
    isFirstBooking: boolean;
};

const toIsoLocal = (value: Date | string | undefined): string | null => {
    if (value == null) return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
};

/**
 * Fetches lightweight booking stats for PostHog (mirrors Flutter `UserBloc._fetchBookingStats`).
 */
export async function fetchUserBookingStatsForTracking(
    userId: number,
    token: string,
    languageCode: string
): Promise<UserBookingStatsForTracking> {
    const empty: UserBookingStatsForTracking = {
        totalBookings: 0,
        firstBookingDate: null,
        lastBookingDate: null,
        isFirstBooking: true
    };

    if (!userId || !token) return empty;

    try {
        const apiConfig = await new ApiConfigration().getApiConfig(languageCode, token);
        const bookingService = new BookingService(apiConfig);

        const first = await bookingService.bookingList({
            user: String(userId),
            limit: 1,
            orderBy: 'created_at'
        });

        const total = first.count ?? 0;
        const isFirstBooking = total === 0;
        let firstBookingDate: string | null = null;
        let lastBookingDate: string | null = null;

        if (first.results?.length) {
            firstBookingDate = toIsoLocal(
                (first.results[0] as Booking).createdAt
            );
        }

        if (total > 1) {
            const last = await bookingService.bookingList({
                user: String(userId),
                limit: 1,
                orderBy: '-created_at'
            });
            if (last.results?.length) {
                lastBookingDate = toIsoLocal(
                    (last.results[0] as Booking).createdAt
                );
            }
        } else {
            lastBookingDate = firstBookingDate;
        }

        return {
            totalBookings: total,
            firstBookingDate,
            lastBookingDate,
            isFirstBooking
        };
    } catch {
        // Non-fatal: stats remain null; tracking still proceeds (Flutter parity).
        return empty;
    }
}
